import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Grid, Typography, Box } from "@mui/material";
import React, { useEffect } from "react";
import { useStore } from "../../app/stores/store";
import PaymentForm from "./PaymentForm";
import DataTable, { DataType } from "../../app/common/DataTable";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { usePaymentMethodOptions } from "../../app/common/enums/paymentType";
import { useTranslation } from "react-i18next";
import agent from "../../app/api/agent";

export default function UnLinkedPaymentsTable() {
  const { t } = useTranslation();
  const modalStore = useStore((s) => s.modalStore);
  const paymentStore = useStore((s) => s.paymentStore);
  const columns = [
    {
      key: "paymentMethod",
      header: t("unLinkedPayments.columns.paymentMethod"),
      options: usePaymentMethodOptions(),
      dataType: DataType.SELECT,
      filterable: true,
    },
    {
      key: "iban",
      header: t("unLinkedPayments.columns.iban"),
      dataType: DataType.TEXT,
      filterable: true,
    },
    {
      key: "paidAt",
      header: t("unLinkedPayments.columns.paidAt"),
      dataType: DataType.DATE,
    },
    {
      key: "paidAmount",
      header: t("unLinkedPayments.columns.paidAmount"),
      dataType: DataType.NUMBER,
      formatPrice: true,
    },
    {
      key: "reasonForPayment",
      header: t("unLinkedPayments.columns.reasonForPayment"),
      filterable: true,
    },
    {
      key: "note",
      header: t("unLinkedPayments.columns.note"),
      filterable: true,
    },
    {
      key: "createdAt",
      header: t("unLinkedPayments.columns.createdAt"),
      dataType: DataType.DATE,
    },
  ];

  const handleDelete = async (id: string) => {
    await paymentStore.deletePayment(id);
    modalStore.closeModal();
  };

  const actionButtons = [
    {
      icon: <DeleteIcon color="error" />,
      onClick: (event: React.MouseEvent, item: any) => {
        event.stopPropagation();
        modalStore.openModal(
          <ConfirmComponent
            onYes={async () => handleDelete(item.id!)}
            onCancel={modalStore.closeModal}
          />
        );
      },
    },
  ];

  useEffect(() => {
    paymentStore.loadUnLinkedPayments();
  }, [paymentStore.loadUnLinkedPayments]);

  const onLinkPaymentsClick = () => {
    modalStore.openModal(
      <ConfirmComponent
        onYes={async () => {
          modalStore.closeModal();
          await paymentStore.connectUnLinkedPayments();
        }}
        onCancel={modalStore.closeModal}
      />
    );
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 6 }} sx={{ textAlign: "right" }}>
          <Typography variant="h4" component="h2">
            {t("unLinkedPayments.pageTitle")}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ textAlign: "right" }}>
          <Button
            disabled={paymentStore.loading}
            onClick={onLinkPaymentsClick}
            variant="contained"
            color="success"
          >
            {t("unLinkedPayments.linkPaymentsButton")}
          </Button>
        </Grid>
      </Grid>

      {paymentStore.loading && (
        <LoadingComponent content={t("unLinkedPayments.connectingPayments")} />
      )}

      <DataTable
        columns={columns}
        data={paymentStore.tableData}
        onClick={(item) =>
          modalStore.openModal(<PaymentForm id={item.id} />, "small")
        }
        actionButtons={actionButtons}
        onFilter={paymentStore.loadUnLinkedPayments}
        defaultOrderByColumn="iban"
        exportAgentMethod={agent.Payments.exportXlsx}
      />
    </>
  );
}
